// Provider-operation recovery cases (SM-661, SM-621 phase 14) — the case
// queue, one case's evidence and history, and the six audited operator
// actions.
//
// A case is broker-service's durable record of a provider operation (or a
// funding intent) that automation could not finish safely on its own. The
// console never decides anything about one: broker-service lists the
// actions it would accept NOW (eligible_actions) and re-runs the
// eligibility when the action arrives — a refusal is 409
// RECOVERY_ACTION_NOT_ELIGIBLE naming its reason. Every action carries a
// note and nothing else. There is deliberately no amount, balance or
// transaction id input anywhere on these screens: no operator control can
// name money, replace a transaction id or activate an unfunded order.
//
// Reads need BROKER_PROVIDER_CASE_VIEW (the page asks for nothing
// without it); the action buttons need BROKER_PROVIDER_RECOVERY_ACT.
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CopyButton } from "@/components/ui/CopyButton";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { Textarea } from "@/components/ui/Textarea";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { APIError } from "@/lib/api";
import {
  auditEventIDs,
  brokerKeys,
  fetchRecoveryCase,
  fetchRecoveryCases,
  takeRecoveryAction,
  type AuditedResult,
  type RecoveryCaseFilter,
} from "@/lib/broker-queries";
import {
  RECOVERY_ACTIONS,
  RECOVERY_CASE_KINDS,
  RECOVERY_CASE_SEVERITIES,
  type RecoveryAction,
  type RecoveryActionItem,
  type ProviderOperationAttemptItem,
  type RecoveryActionResponse,
  type RecoveryCaseItem,
} from "@/lib/broker-types";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

import { ActionDialog } from "./ActionDialog";
import { AuditReference, RefusalNotice } from "./BrokerNotices";
import { brokerErrorDetail, outcomeUnknown } from "./governance";
import {
  RECOVERY_ACTION_COPY,
  attemptEndpointLabel,
  attemptMovesMoney,
  attemptOutcomeLabel,
  attemptOutcomeTone,
  classifierVerdict,
  eligibleActions,
  evidenceJSON,
  recoveryKindLabel,
  recoveryQueueLabel,
  recoveryResultLabel,
  recoveryResultTone,
  recoverySeverityLabel,
  recoverySeverityTone,
  recoveryStateLabel,
  recoveryStateTone,
} from "./providerRecovery";

export const RECOVERY_PAGE_SIZE = 20;

/** ?state=ALL widens to every state; absent means the OPEN default. */
export const RECOVERY_ANY_STATE = "ALL";

export interface RecoveryCaseSearch {
  state?: string;
  severity?: string;
  kind?: string;
  page?: number;
}

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

const STATE_OPTIONS = [
  { value: "OPEN", label: "Open cases" },
  { value: "RESOLVED", label: "Resolved cases" },
  { value: RECOVERY_ANY_STATE, label: "Any state" },
];

const SEVERITY_OPTIONS = RECOVERY_CASE_SEVERITIES.map((s) => ({ value: s, label: recoverySeverityLabel(s) }));
const KIND_OPTIONS = RECOVERY_CASE_KINDS.map((k) => ({ value: k, label: recoveryKindLabel(k) }));

function NoAccess() {
  return (
    <EmptyState
      title="No access"
      description="You need the BROKER_PROVIDER_CASE_VIEW permission to see provider recovery cases."
    />
  );
}

/** A shortened id with the full value on hover and a copy button. */
function Id({ value }: { value?: string }) {
  if (!value) return <span className="text-muted">—</span>;
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <span className="truncate font-mono text-2xs" title={value}>
        {value}
      </span>
      <CopyButton value={value} label="this id" />
    </span>
  );
}

// ── the queue ────────────────────────────────────────────────────────

export function RecoveryCaseList({
  search,
  onSearch,
  onOpen,
}: {
  search: RecoveryCaseSearch;
  /** Merge into the URL search (filters reset the page). */
  onSearch: (next: Partial<RecoveryCaseSearch>) => void;
  onOpen: (caseId: string) => void;
}) {
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerProviderCaseView);
  const stateFilter = search.state ?? "OPEN";
  const page = search.page ?? 0;
  const filter: RecoveryCaseFilter = {
    state: stateFilter === RECOVERY_ANY_STATE ? undefined : stateFilter,
    severity: search.severity,
    kind: search.kind,
    limit: RECOVERY_PAGE_SIZE,
    offset: page * RECOVERY_PAGE_SIZE,
  };
  const q = useQuery({
    queryKey: brokerKeys.recoveryCases(filter),
    queryFn: () => fetchRecoveryCases(filter),
    placeholderData: keepPreviousData,
    // Cases open and resolve on broker-service's own workers.
    refetchInterval: 30_000,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  if (perms.loaded && !canView) return <NoAccess />;

  const narrowed = stateFilter !== "OPEN" || Boolean(search.severity || search.kind);
  const apply = (next: Partial<RecoveryCaseSearch>) => onSearch({ ...next, page: undefined });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Provider recovery cases</h1>
        <p className="text-sm text-muted">
          Provider operations and order fundings that automation could not finish safely on its own. Each case keeps
          the evidence it was opened with. Operators act only through the audited recovery actions, which take a note
          and nothing else — never an amount, a balance or a transaction id.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Case state"
            value={stateFilter}
            onChange={(e) => apply({ state: e.target.value === "OPEN" ? undefined : e.target.value })}
            options={STATE_OPTIONS}
          />
        </div>
        <div className="w-44">
          <Select
            label="Severity"
            value={search.severity ?? ""}
            onChange={(e) => apply({ severity: e.target.value || undefined })}
            placeholder="Any severity"
            options={SEVERITY_OPTIONS}
          />
        </div>
        <div className="w-64">
          <Select
            label="Case kind"
            value={search.kind ?? ""}
            onChange={(e) => apply({ kind: e.target.value || undefined })}
            placeholder="Any kind"
            options={KIND_OPTIONS}
          />
        </div>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Recovery cases</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {(q.isLoading || !perms.loaded) && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {q.data && rows.length === 0 && (
            <EmptyState
              className="m-5"
              title={narrowed ? "No recovery cases match these filters" : "No recovery cases"}
              description={
                narrowed
                  ? "Widen the case state, severity or kind filter to see more."
                  : "Nothing needs a human: every provider operation has finished or is still inside its automated recovery. Resolved cases are under the case state filter."
              }
            />
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Kind</th>
                    <th className={th}>Severity</th>
                    <th className={th}>Owner queue</th>
                    <th className={th}>Broker and account</th>
                    <th className={th}>Order ID</th>
                    <th className={th}>Transaction ID</th>
                    <th className={th}>Opened</th>
                    <th className={th}>Case state</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.case_id}
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60"
                      onClick={() => onOpen(c.case_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onOpen(c.case_id);
                      }}
                      tabIndex={0}
                      data-case-id={c.case_id}
                    >
                      <td className={`${td} px-5`}>
                        <div className="font-medium text-t-1">{recoveryKindLabel(c.kind)}</div>
                        <div className="font-mono text-2xs text-muted">{c.case_id}</div>
                      </td>
                      <td className={td}>
                        <Badge tone={recoverySeverityTone(c.severity)}>{recoverySeverityLabel(c.severity)}</Badge>
                      </td>
                      <td className={td}>{recoveryQueueLabel(c.owner_queue)}</td>
                      <td className={td}>
                        <div className="font-mono text-2xs">{c.broker_id}</div>
                        <div className="font-mono text-2xs text-muted">{c.broker_account_id}</div>
                      </td>
                      <td className={`${td} font-mono text-2xs`}>{c.order_id || "—"}</td>
                      <td className={`${td} max-w-[14rem] truncate font-mono text-2xs`} title={c.transaction_id}>
                        {c.transaction_id || "—"}
                      </td>
                      <td className={`${td} text-2xs text-muted`}>{fmtDate(c.opened_at)}</td>
                      <td className={td}>
                        <Badge tone={recoveryStateTone(c.state)}>{recoveryStateLabel(c.state)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {q.data && q.data.page.total > 0 && (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => onSearch({ page: p > 0 ? p : undefined })}
              itemLabel="entries"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ── one case ─────────────────────────────────────────────────────────

/** One label / value row of a facts list. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/50 py-1.5 last:border-0">
      <dt className="shrink-0 text-2xs text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-xs text-t-1">{children}</dd>
    </div>
  );
}

const orDash = (v: string | number | undefined | null) =>
  v === undefined || v === null || v === "" ? <span className="text-muted">—</span> : v;

const mono = (v: string | undefined) => (v ? <span className="font-mono text-2xs">{v}</span> : <span className="text-muted">—</span>);

export function RecoveryCaseDetail({ caseId }: { caseId: string }) {
  const perms = useMyPerms();
  const admins = useAdminMap();
  const canView = perms.has(Perm.BrokerProviderCaseView);
  const canAct = perms.has(Perm.BrokerProviderRecoveryAct);
  const [acting, setActing] = useState<RecoveryAction | null>(null);

  const q = useQuery({
    queryKey: brokerKeys.recoveryCase(caseId),
    queryFn: () => fetchRecoveryCase(caseId),
    enabled: canView,
  });

  if (perms.loaded && !canView) return <NoAccess />;
  const c = q.data;
  const eligible = eligibleActions(c);
  const verdict = c ? classifierVerdict(c.evidence) : undefined;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/brokers/provider-cases" className="text-2xs text-neon hover:underline">
          All recovery cases
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">{c ? recoveryKindLabel(c.kind) : "Recovery case"}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="font-mono text-2xs text-muted">{caseId}</span>
          {c ? (
            <>
              <Badge tone={recoverySeverityTone(c.severity)}>{recoverySeverityLabel(c.severity)}</Badge>
              <Badge tone={recoveryStateTone(c.state)}>{recoveryStateLabel(c.state)}</Badge>
            </>
          ) : null}
        </div>
      </div>

      {q.isLoading || !perms.loaded ? <Skeleton className="h-40" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}

      {c ? (
        <>
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Recovery actions</CardTitle>
              <span className="text-2xs text-muted">Each is re-checked by broker-service when it arrives</span>
            </CardHeader>
            <CardBody className="space-y-3">
              {canAct ? (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Recovery actions">
                  {RECOVERY_ACTIONS.map((a) => {
                    const ok = eligible.has(a);
                    return (
                      <Button
                        key={a}
                        variant={ok ? (RECOVERY_ACTION_COPY[a].tone === "danger" ? "danger" : "primary") : "ghost"}
                        size="sm"
                        disabled={!ok}
                        title={ok ? RECOVERY_ACTION_COPY[a].description : "Not eligible for this case right now"}
                        onClick={() => setActing(a)}
                        data-action={a}
                      >
                        {RECOVERY_ACTION_COPY[a].label}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted">
                  You can read this case. Acting on it needs the BROKER_PROVIDER_RECOVERY_ACT permission.
                </p>
              )}
              {canAct && eligible.size === 0 ? (
                <p className="text-2xs text-muted">broker-service accepts no action on this case right now.</p>
              ) : null}
            </CardBody>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Case</CardTitle>
              </CardHeader>
              <CardBody>
                <dl>
                  <Fact label="Case ID">
                    <Id value={c.case_id} />
                  </Fact>
                  <Fact label="Owner queue">{recoveryQueueLabel(c.owner_queue)}</Fact>
                  <Fact label="Recovery path">{mono(c.recovery_path)}</Fact>
                  <Fact label="Broker">{mono(c.broker_id)}</Fact>
                  <Fact label="Broker account">{mono(c.broker_account_id)}</Fact>
                  <Fact label="Currency">{orDash(c.currency)}</Fact>
                  <Fact label="Funding intent">
                    <Id value={c.intent_id} />
                  </Fact>
                  <Fact label="Order ID">
                    <Id value={c.order_id} />
                  </Fact>
                  <Fact label="Transaction ID">
                    <Id value={c.transaction_id} />
                  </Fact>
                  <Fact label="Opened">{fmtDate(c.opened_at)}</Fact>
                  <Fact label="Resolved at">{c.resolved_at ? fmtDate(c.resolved_at) : orDash(undefined)}</Fact>
                  <Fact label="Resolution action">{mono(c.resolution_action)}</Fact>
                  <Fact label="Resolution note">{orDash(c.resolution_note)}</Fact>
                </dl>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Evidence</CardTitle>
                <span className="text-2xs text-muted">What was known when the case opened</span>
              </CardHeader>
              <CardBody className="space-y-3">
                <dl>
                  <Fact label="Classifier verdict">{mono(verdict)}</Fact>
                </dl>
                <pre
                  className="max-h-80 overflow-auto rounded-md bg-surface-2 p-3 font-mono text-2xs leading-relaxed text-t-2"
                  aria-label="Evidence JSON"
                >
                  {evidenceJSON(c.evidence)}
                </pre>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Order funding</CardTitle>
              </CardHeader>
              <CardBody>
                {c.funding ? (
                  <dl>
                    <Fact label="Funding state">{mono(c.funding.state)}</Fact>
                    <Fact label="Resolution">{mono(c.funding.resolution)}</Fact>
                    <Fact label="Release reason">{mono(c.funding.release_reason)}</Fact>
                    <Fact label="Released at">{c.funding.released_at ? fmtDate(c.funding.released_at) : orDash(undefined)}</Fact>
                    <Fact label="Release deadline">{c.funding.deadline_at ? fmtDate(c.funding.deadline_at) : orDash(undefined)}</Fact>
                    <Fact label="Side">{mono(c.funding.side)}</Fact>
                    <Fact label="Required funding">{mono(c.funding.required_funding_provider)}</Fact>
                    <Fact label="Provider transaction">
                      <Id value={c.funding.provider_transaction_id} />
                    </Fact>
                    <Fact label="Reject code">{mono(c.funding.reject_code)}</Fact>
                    <Fact label="Provider code">{mono(c.funding.provider_code)}</Fact>
                    <Fact label="Order notified">{c.funding.order_notified ? <>Yes</> : <>No</>}</Fact>
                  </dl>
                ) : (
                  <p className="text-xs text-muted">No order funding is linked to this case.</p>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Provider operation</CardTitle>
              </CardHeader>
              <CardBody>
                {c.operation ? (
                  <dl>
                    <Fact label="Operation">{mono(c.operation.kind)}</Fact>
                    <Fact label="Provider reason">{mono(c.operation.reason)}</Fact>
                    <Fact label="Transaction ID">
                      <Id value={c.operation.transaction_id} />
                    </Fact>
                    <Fact label="Local state">{mono(c.operation.local_state)}</Fact>
                    <Fact label="Provider status">{mono(c.operation.provider_status)}</Fact>
                    <Fact label="Attempts">{orDash(c.operation.attempts)}</Fact>
                    <Fact label="Failure code">{mono(c.operation.failure_code)}</Fact>
                    <Fact label="First attempt">{c.operation.first_attempt_at ? fmtDate(c.operation.first_attempt_at) : orDash(undefined)}</Fact>
                    <Fact label="Last attempt">{c.operation.last_attempt_at ? fmtDate(c.operation.last_attempt_at) : orDash(undefined)}</Fact>
                    <Fact label="Next automated recovery">
                      {c.operation.next_recovery_at ? fmtDate(c.operation.next_recovery_at) : orDash(undefined)}
                    </Fact>
                  </dl>
                ) : (
                  <p className="text-xs text-muted">No provider operation is linked to this case.</p>
                )}
              </CardBody>
            </Card>
          </div>

          {c.operation ? (
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Provider attempt log</CardTitle>
                <span className="text-2xs text-muted">{(c.operation.attempt_log ?? []).length}</span>
              </CardHeader>
              <CardBody className="p-0">
                {(c.operation.attempt_log ?? []).length === 0 ? (
                  <p className="px-5 py-8 text-center text-xs text-muted">
                    No provider request is recorded for this operation yet.
                  </p>
                ) : (
                  <AttemptLog items={c.operation.attempt_log ?? []} />
                )}
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Action history</CardTitle>
              <span className="text-2xs text-muted">{(c.actions ?? []).length}</span>
            </CardHeader>
            <CardBody className="p-0">
              {(c.actions ?? []).length === 0 ? (
                <p className="px-5 py-8 text-center text-xs text-muted">No operator has acted on this case yet.</p>
              ) : (
                <ActionHistory items={c.actions ?? []} name={(id) => adminName(admins, id)} />
              )}
            </CardBody>
          </Card>

          {acting ? <RecoveryActionDialog caseItem={c} action={acting} onClose={() => setActing(null)} /> : null}
        </>
      ) : null}
    </div>
  );
}

function ActionHistory({ items, name }: { items: RecoveryActionItem[]; name: (id: string) => string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-surface-2">
          <tr>
            <th className={`${th} px-5`}>When</th>
            <th className={th}>Action</th>
            <th className={th}>Result</th>
            <th className={th}>Admin</th>
            <th className={th}>Target transaction</th>
            <th className={th}>Detail</th>
            <th className={th}>Note</th>
            <th className={th}>Request ID</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.action_id} className="border-b border-border/60 align-top last:border-0">
              <td className={`${td} px-5 text-2xs text-muted`}>{fmtDate(a.created_at)}</td>
              <td className={td}>
                {RECOVERY_ACTION_COPY[a.action as RecoveryAction]?.label ?? <span className="font-mono">{a.action}</span>}
              </td>
              <td className={td}>
                <Badge tone={recoveryResultTone(a.result)}>{recoveryResultLabel(a.result)}</Badge>
              </td>
              <td className={td}>{name(a.admin_id)}</td>
              <td className={`${td} font-mono text-2xs`}>{a.target_transaction_id || "—"}</td>
              <td className={`${td} text-2xs`}>{a.detail || "—"}</td>
              <td className={`${td} text-2xs`}>{a.note || "—"}</td>
              <td className={`${td} font-mono text-2xs text-muted`}>{a.request_id || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every provider request of the operation, oldest first: which endpoint it
 * called (a status read is never mistaken for the money call), its request
 * id, the classifier's verdict and what the provider answered.
 */
function AttemptLog({ items }: { items: ProviderOperationAttemptItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="Provider attempt log">
        <thead className="border-b border-border bg-surface-2">
          <tr>
            <th className={`${th} px-5`}>Started</th>
            <th className={th}>Endpoint</th>
            <th className={th}>Request ID</th>
            <th className={th}>Outcome</th>
            <th className={th}>HTTP status</th>
            <th className={th}>Provider code</th>
            <th className={th}>Provider status</th>
            <th className={`${th} text-right`}>Latency (ms)</th>
            <th className={th}>Key version</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.attempt_id} className="border-b border-border/60 align-top last:border-0" data-attempt-id={a.attempt_id}>
              <td className={`${td} px-5 text-2xs text-muted`}>{fmtDate(a.started_at)}</td>
              <td className={td}>
                <div className={attemptMovesMoney(a.endpoint) ? "font-medium text-t-1" : "text-t-2"}>
                  {attemptEndpointLabel(a.endpoint)}
                </div>
                <div className="font-mono text-2xs text-muted">{a.endpoint || "—"}</div>
              </td>
              <td className={`${td} max-w-[14rem]`}>
                <Id value={a.request_id} />
              </td>
              <td className={td}>
                <Badge tone={attemptOutcomeTone(a.outcome_class)}>{attemptOutcomeLabel(a.outcome_class)}</Badge>
              </td>
              <td className={`${td} font-mono text-2xs`}>{orDash(a.http_status)}</td>
              <td className={td}>{mono(a.provider_code)}</td>
              <td className={td}>{mono(a.provider_status)}</td>
              <td className={`${td} text-right font-mono text-2xs`}>{a.latency_ms}</td>
              <td className={td}>
                {mono(a.key_version)}
                {a.environment ? <div className="font-mono text-2xs text-muted">{a.environment}</div> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── the confirmation ─────────────────────────────────────────────────

const RESULT_HEADINGS: Record<string, string> = {
  ACCEPTED: "Action accepted",
  DONE: "Action done",
  REFUSED: "Action refused",
};

/**
 * Confirms one action. The only field is the note; the request body is
 * {note} (or {} without one) and nothing else.
 */
export function RecoveryActionDialog({
  caseItem,
  action,
  onClose,
}: {
  caseItem: RecoveryCaseItem;
  action: RecoveryAction;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const copy = RECOVERY_ACTION_COPY[action];
  const [note, setNote] = useState("");
  const [result, setResult] = useState<AuditedResult<RecoveryActionResponse> | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: brokerKeys.all });
  const run = useMutation({
    mutationFn: () => takeRecoveryAction(caseItem.case_id, action, note),
    onSuccess: async (res) => {
      setResult(res);
      await refresh();
    },
    // A refusal is recorded too (broker-service's action row, backoffice's
    // audit row): reload so the history shows it.
    onError: () => void refresh(),
  });

  useEffect(() => {
    run.reset();
    setNote("");
    setResult(null);
    // Reset when the dialog opens for another action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, caseItem.case_id]);

  const unknown = outcomeUnknown(run.error);
  const noteOk = !copy.noteRequired || note.trim() !== "";
  const refusedReason = brokerErrorDetail(run.error)?.details?.reason;
  const refused = run.error instanceof APIError && run.error.status === 409;
  const answered = result?.data?.action;

  return (
    <ActionDialog
      open
      title={copy.title}
      tone={copy.tone}
      successMessage={answered ? RESULT_HEADINGS[answered.result] ?? "Action answered" : undefined}
      description={
        <div className="space-y-2">
          <p>{copy.description}</p>
          <dl className="rounded-md bg-surface-2 px-3 py-2">
            <Fact label="Case">{recoveryKindLabel(caseItem.kind)}</Fact>
            <Fact label="Case ID">{mono(caseItem.case_id)}</Fact>
            <Fact label="Transaction ID">{mono(caseItem.transaction_id)}</Fact>
          </dl>
        </div>
      }
      confirmLabel={copy.label}
      canSubmit={!unknown && noteOk}
      loading={run.isPending}
      onSubmit={() => run.mutate()}
      onCancel={onClose}
      error={
        run.error ? (
          <div className="space-y-2">
            {refused ? <Badge tone="danger">Refused</Badge> : null}
            <RefusalNotice error={run.error} />
            {refusedReason ? (
              <p className="flex flex-wrap gap-2 text-2xs text-muted">
                <span>Reason</span>
                <span className="font-mono text-t-1">{refusedReason}</span>
              </p>
            ) : null}
            <AuditReference ids={run.error instanceof APIError ? auditEventIDs(run.error.headers) : []} />
          </div>
        ) : null
      }
      result={
        result ? (
          <>
            <dl className="rounded-md bg-surface-2 px-3 py-2">
              <Fact label="Result">
                <Badge tone={recoveryResultTone(answered?.result ?? "")}>{recoveryResultLabel(answered?.result ?? "—")}</Badge>
              </Fact>
              <Fact label="Detail">{orDash(answered?.detail)}</Fact>
              <Fact label="Target transaction">{mono(answered?.target_transaction_id)}</Fact>
              <Fact label="Action ID">{mono(answered?.action_id)}</Fact>
              <Fact label="Case state">
                {result.data?.case ? recoveryStateLabel(result.data.case.state) : orDash(undefined)}
              </Fact>
            </dl>
            <AuditReference ids={result.auditEventIds} />
          </>
        ) : undefined
      }
    >
      <Textarea
        label={copy.noteRequired ? "Note (required, audit log)" : "Note (audit log)"}
        placeholder="What you checked, and why this action"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        hint={copy.noteRequired ? "Say what shows the recovery is complete." : undefined}
      />
      <p className="text-2xs text-muted">
        The note is the only thing this action sends. Amounts, balances and transaction ids are never taken here.
      </p>
    </ActionDialog>
  );
}
